import { findIndex, get, isEmpty, isEqual, orderBy } from 'lodash'
import PropTypes from 'prop-types'
import { Component } from 'react'
import { FormattedMessage } from 'react-intl'
import * as Table from 'reactabular-table'
import { compose } from 'redux'
import * as select from 'selectabular'
import * as sort from 'sortabular'
import * as resolve from 'table-resolver'

import cn from '@/utilities/cn'

import Paginator from './Paginator'

import './Pagination.scss'

import messages from './messages'

class PaginationTable extends Component<any, any> {
  static displayName = 'PaginationTable'

  static propTypes = {
    className: PropTypes.string,
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        property: PropTypes.string.isRequired,
        cell: PropTypes.shape({
          property: PropTypes.string,
          props: PropTypes.object,
          formatters: PropTypes.array,
          transforms: PropTypes.array
        }),
        header: PropTypes.shape({
          label: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
          sortable: PropTypes.bool,
          formatters: PropTypes.array,
          transforms: PropTypes.array
        }),
        props: PropTypes.object
      })
    ).isRequired,
    /*
     * onRow can be used to support recent add highlighting
     * Example:
     *  row => {
     *     return {
     *        className: somePropFromParentComponent === row.value  ? 'animate-enter' : ''
     *     }
     *  }
     */
    onRow: PropTypes.func,
    // @deprecated - This functionality actually doesn't work, A11Y wise you should use buttons/links instead anyway
    onSelectRow: PropTypes.func,
    pagination: PropTypes.shape({
      isClientPaging: PropTypes.bool,
      count: PropTypes.number.isRequired,
      perPage: PropTypes.number.isRequired,
      startIndex: PropTypes.number.isRequired
    }),
    renderers: PropTypes.object,
    rows: PropTypes.array,
    rowItemType: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
    rowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
    sortingColumn: PropTypes.object,
    onPaginateSelect: PropTypes.func,
    showHeaderOnEmpty: PropTypes.bool,
    footerMessage: PropTypes.string
  }

  static defaultProps = {
    className: '',
    pagination: null,
    renderers: {
      table: 'table',
      header: {
        wrapper: 'thead',
        row: 'tr',
        cell: 'th'
      },
      body: {
        wrapper: 'tbody',
        row: 'tr',
        cell: 'td'
      }
    },
    onPaginateSelect: null,
    onSelectRow: null,
    onRow: null,
    rows: [],
    rowItemType: '',
    sortingColumn: null,
    showHeaderOnEmpty: true
  }

  constructor(props: any) {
    super(props)

    this.state = {
      columns: [],
      selectedRows: [],
      sortingColumn: this.props.sortingColumn || {},
      paginationState: this.setPaginateState(false)
    }
  }

  componentDidMount() {
    this.setState({ columns: this.generateColumns(this.props.columns), paginationState: this.setPaginateState(true) })
  }

  componentDidUpdate(prevProps: any) {
    if (!isEqual(prevProps.pagination, this.props.pagination) || prevProps.rows !== this.props.rows) {
      this.setState({ paginationState: this.setPaginateState(true) })
    }
    if (prevProps.columns !== this.props.columns) {
      this.setState({ columns: this.generateColumns(this.props.columns) })
    }
    if (prevProps.sortingColumn !== this.props.sortingColumn) {
      this.setState({ sortingColumn: this.props.sortingColumn })
    }
  }

  generateColumns = (columns: any) => {
    const getSortingColumns = () => this.state.sortingColumn || {}
    const sortable = sort.sort({
      getSortingColumns,
      onSort: (selectedColumn: any) => {
        this.setState(
          {
            sortingColumn: sort.byColumn({
              sortingColumns: this.state.sortingColumn,
              sortingOrder: { FIRST: 'asc', asc: 'desc', desc: 'asc' },
              selectedColumn
            })
          },
          () => {
            this.getPage(1, selectedColumn)
          }
        )
      },
      strategy: sort.strategies.byProperty
    })

    return columns.map((column: any) => {
      if (!!column.header && column.header.sortable) {
        column.header.transforms = [sortable]
        column.header.formatters = [
          sort.header({
            sortable,
            getSortingColumns,
            strategy: sort.strategies.byProperty
          })
        ]
      }
      return column
    })
  }

  getPage = (page: any, selectedColumn?: any) => {
    if (this.props.onPaginateSelect) {
      if (selectedColumn) {
        const sortColumn = this.state.sortingColumn
        const sortColumnObject = get(sortColumn, selectedColumn)
        const sortDirection = sortColumnObject ? sortColumnObject.direction : null
        this.props.onPaginateSelect(page, selectedColumn, sortDirection ? sortDirection.toUpperCase() : null)
      } else if (this.state.sortingColumn && !isEmpty(this.state.sortingColumn)) {
        this.props.onPaginateSelect(
          page,
          Object.keys(this.state.sortingColumn)[0],
          this.state.sortingColumn[Object.keys(this.state.sortingColumn)[0]].direction.toUpperCase()
        )
      } else {
        this.props.onPaginateSelect(page)
      }
    } else {
      this.setState({ paginationState: { ...this.state.paginationState, page: page } })
    }
  }

  onPagination = (page: any) => {
    this.getPage(page)
  }

  onRow = (row: any, { rowIndex }: any) => {
    if (this.props.onRow) {
      return this.props.onRow(row)
    } else {
      return {
        className: cn(row.selected && 'selected-row'),
        onClick: () => this.onSelectRow(rowIndex)
      }
    }
  }

  paginate = ({ page, perPage }: any) => {
    return (rows: number[] = []) => {
      // adapt to zero indexed logic
      const p = page - 1 || 0

      const amountOfPages = Math.ceil(rows.length / perPage)
      const startPage = p < amountOfPages ? p : 0

      return {
        amount: amountOfPages,
        rows: rows.slice(startPage * perPage, startPage * perPage + perPage),
        page: startPage
      }
    }
  }

  refineRows = (rowItems: any) => {
    const { columns, paginationState, sortingColumn, selectedRowIndex } = this.state
    const { pagination, onPaginateSelect, rowKey } = this.props
    const resolvedColumns = resolve.columnChildren({ columns })
    const selectedRowId =
      selectedRowIndex >= 0 && selectedRowIndex < rowItems.length && rowKey ? rowItems[selectedRowIndex][rowKey] : -1
    let refinedData = null
    if (pagination && (!onPaginateSelect || pagination.isClientPaging)) {
      refinedData = compose(
        this.paginate(paginationState),
        sort.sorter({
          columns: resolvedColumns,
          sortingColumns: sortingColumn,
          sort: orderBy,
          strategy: sort.strategies.byProperty
        }),
        resolve.resolve({
          columns: resolvedColumns,
          method: resolve.nested
        })
      )(rowItems)
    } else if (pagination) {
      refinedData = compose(
        this.paginate(paginationState),
        resolve.resolve({
          columns: resolvedColumns,
          method: resolve.nested
        })
      )(rowItems)
    } else if (onPaginateSelect) {
      refinedData = compose(
        resolve.resolve({
          columns: resolvedColumns,
          method: resolve.nested
        })
      )(rowItems)
    } else {
      refinedData = compose(
        sort.sorter({
          columns: resolvedColumns,
          sortingColumns: sortingColumn,
          sort: orderBy,
          strategy: sort.strategies.byProperty
        }),
        resolve.resolve({
          columns: resolvedColumns,
          method: resolve.nested
        })
      )(rowItems)
    }
    return compose(select.rows((row: any) => row[rowKey] === selectedRowId))(
      // @ts-expect-error
      refinedData.rows || refinedData
    )
  }

  setPaginateState = (update: any) => {
    if (this.props.pagination) {
      const { count, perPage, startIndex } = this.props.pagination
      const currentPage = Math.ceil(startIndex / perPage)

      return {
        page: currentPage,
        perPage: perPage,
        start: update ? (currentPage - 1) * perPage + 1 : 1,
        end: Math.min(currentPage * perPage, count)
      }
    }
  }

  onSelectRow = (selectedRowIndex: any) => {
    if (this.props.onSelectRow) {
      this.setState({
        selectedRowIndex: selectedRowIndex
      })
      this.props.onSelectRow(this.getSelectedRow(selectedRowIndex))
    }
  }

  getSelectedRowIndex(selectedRows: any) {
    const { rows, rowKey } = this.props
    return findIndex(this.refineRows(rows), {
      [rowKey]: selectedRows[0] && selectedRows[0][rowKey]
    })
  }

  getSelectedRow(selectedRowIndex: any) {
    const { rows } = this.props
    return rows[selectedRowIndex]
  }

  render() {
    const { columns, paginationState } = this.state
    const { className, pagination, rows, rowKey, rowItemType, onSelectRow, showHeaderOnEmpty, footerMessage } =
      this.props
    const resolvedColumns = resolve.columnChildren({ columns })
    const refinedData = this.refineRows(rows)
    const refinedRows = refinedData.rows

    const showHeader = (showHeaderOnEmpty && rows.length === 0) || rows.length > 0

    return (
      <>
        <Table.Provider
          className={cn('flat-table', 'pagination-table', onSelectRow && 'select-table', className)}
          // @ts-expect-error
          columns={resolvedColumns}
          renderers={this.props.renderers}
        >
          {showHeader && (
            <Table.Header
              // @ts-expect-error
              onClick={(event: any) => event.stopPropagation()}
              headerRows={resolve.headerRows({ columns })}
            />
          )}
          {rows.length > 0 && <Table.Body rows={refinedRows} rowKey={rowKey} onRow={this.onRow} />}
        </Table.Provider>
        {footerMessage && (
          <div className="mt-2 mb-6 flex w-full items-center justify-center font-bold">{footerMessage}</div>
        )}
        {!!pagination && rows.length > 0 && (
          <div className="controls mt-2 hidden justify-between sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <p className="text-base font-medium text-gray-700">
              <FormattedMessage
                {...messages.pagination}
                values={{
                  start: paginationState.start,
                  end: paginationState.end,
                  total: pagination.count,
                  type: rowItemType || ''
                }}
              />
            </p>
            {pagination.count > paginationState.perPage && (
              <Paginator
                pagination={paginationState}
                pages={Math.ceil(pagination.count / paginationState.perPage)}
                onSelect={this.onPagination}
              />
            )}
          </div>
        )}
      </>
    )
  }
}

export default PaginationTable
